/**
 * Authentication Hook
 * Manages authentication state and token validation with auto-recovery
 */

import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useTelegram } from './useTelegram';

export function useAuth() {
  const { initData, isReady } = useTelegram();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Set up auth error callback
  useEffect(() => {
    const handleAuthError = () => {
      console.log('🔒 Auth error detected, clearing state');
      setIsAuthenticated(false);
      setToken(null);
      setError('Authentication expired');
    };

    console.log('🔧 Setting up auth error callback');
    api.setAuthErrorCallback(handleAuthError);

    return () => {
      console.log('🔧 Cleaning up auth error callback');
      api.clearAuthErrorCallback();
    };
  }, []);

  // Check if token exists in localStorage on mount
  useEffect(() => {
    console.log('🔍 Checking for stored auth token...');
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      console.log('✅ Found stored token, setting authenticated state');
      setToken(storedToken);
      setIsAuthenticated(true);
    } else {
      console.log('ℹ️ No stored token found');
    }
  }, []);

  const login = async (initDataString?: string) => {
    try {
      console.log('🔐 Starting login process...');
      setIsLoading(true);
      setError(null);

      const dataToValidate = initDataString || initData;
      
      if (!dataToValidate) {
        console.error('❌ No initData available for authentication');
        throw new Error('No initData available for authentication');
      }

      console.log('📡 Sending auth request to API...');
      const response = await api.validateAuth(dataToValidate);
      
      if (response.data?.token) {
        const authToken = response.data.token;
        console.log('✅ Authentication successful, token received');
        localStorage.setItem('auth_token', authToken);
        setToken(authToken);
        setIsAuthenticated(true);
        setError(null);
        return { success: true, token: authToken };
      } else {
        console.error('❌ No token received from server');
        throw new Error('No token received from server');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Authentication failed';
      console.error('❌ Authentication failed:', errorMessage);
      setError(errorMessage);
      setIsAuthenticated(false);
      setToken(null);
      localStorage.removeItem('auth_token');
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    console.log('🚪 Logging out...');
    localStorage.removeItem('auth_token');
    setToken(null);
    setIsAuthenticated(false);
    setError(null);
  };

  const checkTokenValidity = async () => {
    try {
      // Try to fetch profile to validate token
      await api.getProfile();
      return true;
    } catch (err: any) {
      if (err.response?.status === 401) {
        console.log('🔒 Token validation failed - token is invalid');
        // Don't call logout here as it will be handled by the interceptor
        return false;
      }
      // Other errors don't necessarily mean invalid token
      return true;
    }
  };

  // Auto-login when Telegram WebApp is ready
  useEffect(() => {
    console.log('🔄 Auto-login check:', { 
      isReady, 
      isAuthenticated, 
      hasToken: !!token, 
      hasInitData: !!initData, 
      isLoading
    });
    
    // Only auto-login if:
    // 1. Telegram is ready
    // 2. Not already authenticated
    // 3. No token in storage
    // 4. Have initData from Telegram
    // 5. Not currently loading
    if (isReady && !isAuthenticated && !token && initData && !isLoading) {
      console.log('🚀 Triggering auto-login...');
      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, isAuthenticated, token, initData, isLoading]);

  return {
    isAuthenticated,
    isLoading,
    error,
    token,
    login,
    logout,
    checkTokenValidity,
  };
}
